/**
 * Was mit dem mobilen Erfassungs-Assistenten geschieht, wenn das Projekt
 * WAEHREND der Erfassung dichtmacht.
 *
 * Der Fall ist real: die Projektsperre kann mitten im Tippen kippen — ein
 * Administrator bricht sie auf, oder der eigene Herzschlag bleibt im Funkloch
 * aus und ein anderer uebernimmt (App.tsx, Herzschlag-Effekt). Der Assistent
 * haengt an derselben Nur-Ansicht-Flagge wie alles andere im Modul.
 *
 * Frueher wurde er dabei einfach nicht mehr gerendert. Der Pruefer stand ohne
 * Vorwarnung vor der Modulansicht, und alles, was er gerade erfasst hatte,
 * lag im inneren Zustand einer ausgehaengten Komponente — unwiederbringlich.
 *
 * Die Regel hier ist deshalb bewusst anders als bei den uebrigen Werkzeugen:
 * die Maske BLEIBT stehen, nur das Speichern faellt weg. Damit bleibt die
 * Eingabe sichtbar und kopierbar, und der Grund steht daneben. Kein
 * „welche Version behalten?"-Dialog — den gibt es seit dem Ausbau des
 * Konflikt-Ankers nicht mehr.
 *
 * Rein und ohne React, damit die Verzweigung ohne jsdom pruefbar ist
 * (Projektkonvention wie bei projectLockLogic).
 */

import { ProjectNotice } from './projectLockLogic';
import { LOCK_OHNE_HALTER } from './readOnlyNotice';

/**
 * Was der Nutzer im gesperrten Assistenten tun soll.
 *
 * Muss beides sagen: dass NICHTS gespeichert wird — und dass die Eingabe noch
 * da ist. Ohne den zweiten Halbsatz schliesst er die Maske und verliert genau
 * das, was dieser Hinweis retten soll.
 */
export const WIZARD_EINGABE_BEWAHREN =
    'Dieser Eintrag lässt sich nicht mehr speichern. Die Eingaben bleiben in der Maske stehen — ' +
    'bitte notieren, kopieren oder abfotografieren und nach dem Übernehmen der Bearbeitung erneut erfassen.';

export interface MobileWizardGuard {
    /** Assistent anzeigen? */
    open: boolean;
    /** Darf der Eintrag geschrieben werden? */
    canSave: boolean;
    /** Roter Balken im Assistenten; null = keiner. */
    notice: string | null;
}

const CLOSED: MobileWizardGuard = { open: false, canSave: false, notice: null };
const OFFEN: MobileWizardGuard = { open: true, canSave: true, notice: null };

/**
 * Zustand des Assistenten aus Sichtbarkeit, Schreibschutz und Balkenhinweis.
 *
 * Der Grundtext kommt aus dem fertigen Hinweisbalken des Moduls (`notice`):
 * dort steht bereits „Wird gerade von X bearbeitet (seit 14:32 Uhr)." — der
 * Name ist das, was dem Pruefer weiterhilft. Fehlt der Balken (Einsatz
 * ausserhalb von App.tsx, leerer Text), bleibt es bei der ehrlichen
 * Umschreibung ohne Halter.
 */
export const decideMobileWizardGuard = (
    showWizard: boolean,
    isReadOnly: boolean,
    notice?: ProjectNotice | null,
): MobileWizardGuard => {
    if (!showWizard) return CLOSED;
    if (!isReadOnly) return OFFEN;

    const grund = (notice?.text ?? '').trim();
    return {
        open: true,
        canSave: false,
        notice: `${grund !== '' ? grund : LOCK_OHNE_HALTER} ${WIZARD_EINGABE_BEWAHREN}`,
    };
};
